import { Link } from 'react-router-dom';
import { ArrowRight, Camera, CheckCircle2, Heart, Images, Palette, Sparkles } from 'lucide-react';
import Footer from '@/components/layout/Footer';
import SEOHead from '@/components/seo/SEOHead';
import { Button } from '@/components/ui/button';
import { absoluteUrl, SITE_NAME } from '@/constants/site';
import { trackFunnelEvent } from '@/services/FunnelAnalytics';

const photoIdeas = [
  {
    icon: Heart,
    title: 'Un portrait complice',
    photo: 'Choisissez un moment naturel où les deux visages sont nets, proches et éclairés de façon homogène.',
    style: 'Aquarelle pour la douceur ou peinture à l’huile pour un portrait intemporel.',
    source: 'close_portrait',
  },
  {
    icon: Images,
    title: 'Un souvenir de voyage',
    photo: 'Gardez un décor reconnaissable sans laisser le paysage prendre toute la place autour du couple.',
    style: 'Impressionniste pour conserver la lumière et l’atmosphère du lieu.',
    source: 'travel_memory',
  },
  {
    icon: Camera,
    title: 'Un moment à célébrer',
    photo: 'Mariage, anniversaire ou rencontre : partez d’une image qui raconte immédiatement votre histoire commune.',
    style: 'Huile pour un rendu premium ou croquis pour une composition sobre et élégante.',
    source: 'shared_milestone',
  },
];

const photoChecklist = [
  'Utilisez une photo nette où les deux visages restent assez grands pour préserver les expressions.',
  'Préférez une lumière naturelle et évitez les contre-jours, filtres forts et captures d’écran.',
  'Gardez un peu d’espace autour du couple afin d’adapter le portrait aux formats portrait, paysage ou carré.',
  'Choisissez une image personnelle que la personne reconnaîtra dès le premier regard.',
];

const styleSuggestions = [
  { to: '/styles/watercolor', title: 'Aquarelle', helper: 'Un rendu doux et lumineux pour un portrait romantique ou spontané.' },
  { to: '/styles/oil-painting', title: 'Peinture à l’huile', helper: 'Une esthétique intemporelle pour un portrait à accrocher durablement.' },
  { to: '/styles/impressionist', title: 'Impressionniste', helper: 'Idéal quand un voyage, un paysage ou une lumière fait partie du souvenir.' },
  { to: '/styles/sketch', title: 'Croquis', helper: 'Une interprétation épurée qui met les regards et les silhouettes au premier plan.' },
];

const relatedPages = [
  {
    to: '/cadeau-mariage-photo-personnalise',
    title: 'Cadeau mariage photo',
    helper: 'Trouver une idée à offrir aux mariés à partir d’un souvenir de couple.',
  },
  {
    to: '/portrait-famille-personnalise',
    title: 'Portrait de famille',
    helper: 'Préparer une photo avec enfants, fratrie ou plusieurs générations.',
  },
  {
    to: '/idees-cadeaux-photo-personnalises',
    title: 'Idées cadeaux photo',
    helper: 'Comparer les occasions et les styles avant de créer votre tableau.',
  },
];

const createPath = (source: string) => `/create?source=couple_portrait_${source}`;

const trackCoupleCta = (source: string) => {
  trackFunnelEvent('seo_landing_cta_clicked', {
    page: 'portrait-couple-personnalise',
    source: `couple_portrait_${source}`,
  });
};

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': absoluteUrl('/portrait-couple-personnalise'),
      name: 'Portrait de couple personnalisé',
      description: 'Transformez une photo de couple en portrait personnalisé avec des conseils de cadrage et des styles artistiques adaptés.',
      url: absoluteUrl('/portrait-couple-personnalise'),
      isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: absoluteUrl() },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: absoluteUrl() },
        { '@type': 'ListItem', position: 2, name: 'Portrait de couple personnalisé', item: absoluteUrl('/portrait-couple-personnalise') },
      ],
    },
  ],
};

const PortraitCouplePersonnalise = () => (
  <>
    <SEOHead
      title="Portrait de couple personnalisé à partir d’une photo | Ma Photo en Tableau"
      description="Transformez votre photo en portrait de couple personnalisé. Conseils photo, styles artistiques adaptés et création en ligne de votre tableau."
      canonical={absoluteUrl('/portrait-couple-personnalise')}
      schema={schema}
    />
    <div className="min-h-screen overflow-x-hidden">
      <main>
        <section className="section-shell gradient-hero relative overflow-hidden pb-20 pt-32">
          <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
          <div className="hero-orb right-[12%] top-24 h-40 w-40 bg-primary/25 [animation-delay:2s]" />
          <div className="container relative mx-auto px-6">
            <div className="grid items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
              <div className="max-w-3xl">
                <p className="eyebrow mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-primary/85 shadow-card backdrop-blur-sm">
                  Portrait de couple personnalisé
                </p>
                <h1 className="display-title mb-6 text-foreground">
                  Transformez votre photo en <span className="text-premium">portrait de couple</span>.
                </h1>
                <p className="mb-8 max-w-2xl text-lg leading-8 text-foreground/72 md:text-xl">
                  Portrait complice, souvenir de voyage ou moment à célébrer : choisissez une photo qui raconte votre lien, puis découvrez le style artistique qui lui correspond.
                </p>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button variant="premium" size="lg" className="h-14 rounded-full px-8 text-base shadow-glow" asChild>
                    <Link to={createPath('hero')} onClick={() => trackCoupleCta('hero')}>
                      Créer notre portrait
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="outline" size="lg" className="h-14 rounded-full border-white/70 bg-white/60 px-8 text-base" asChild>
                    <Link to="/idees-cadeaux-photo-personnalises">Voir les idées cadeaux</Link>
                  </Button>
                </div>
              </div>

              <div className="rounded-[2.2rem] border border-white/65 bg-white/65 p-6 shadow-luxury backdrop-blur-xl">
                <img
                  src="/site-assets/hero-wedding.jpg"
                  alt="Portrait artistique d’un couple créé à partir d’une photo"
                  width={800}
                  height={640}
                  className="mb-6 aspect-[5/4] w-full rounded-[1.5rem] object-cover"
                />
                <h2 className="font-display text-4xl leading-none text-foreground">Une photo simple aide à préserver les regards et la complicité.</h2>
                <div className="mt-6 space-y-4">
                  {photoChecklist.map((tip) => (
                    <div key={tip} className="flex gap-3 text-sm leading-7 text-foreground/68">
                      <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-primary" />
                      <span>{tip}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="mb-10 max-w-3xl">
              <p className="eyebrow mb-3">Choisir son souvenir</p>
              <h2 className="section-title text-foreground">Quel portrait de couple créer ?</h2>
              <p className="mt-4 text-lg leading-8 text-foreground/65">Adaptez la photo et le style à l’émotion que vous souhaitez garder ou offrir.</p>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {photoIdeas.map((idea) => (
                <article key={idea.title} className="rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card backdrop-blur-md">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-[1rem] bg-primary/10 text-primary">
                    <idea.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-3xl leading-none text-foreground">{idea.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Photo conseillée :</span> {idea.photo}</p>
                  <p className="mt-3 text-sm leading-7 text-foreground/65"><span className="font-semibold text-foreground">Style :</span> {idea.style}</p>
                  <Button variant="premium" className="mt-6 rounded-full" asChild>
                    <Link to={createPath(idea.source)} onClick={() => trackCoupleCta(idea.source)}>
                      Partir de cette idée
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="mb-10 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div className="max-w-3xl">
                <p className="eyebrow mb-3">Styles conseillés</p>
                <h2 className="section-title text-foreground">Quel style pour votre portrait à deux ?</h2>
              </div>
              <Button variant="outline" className="rounded-full border-white/70 bg-white/60" asChild>
                <Link to={createPath('styles')} onClick={() => trackCoupleCta('styles')}>Tester avec notre photo</Link>
              </Button>
            </div>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {styleSuggestions.map((style) => (
                <Link key={style.to} to={style.to} className="group rounded-[1.6rem] border border-white/65 bg-white/60 p-5 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                  <Palette className="mb-4 h-6 w-6 text-primary" />
                  <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{style.title}</h3>
                  <p className="mt-3 text-sm leading-7 text-foreground/62">{style.helper}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary">Voir le style <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell py-20">
          <div className="container mx-auto px-6">
            <div className="grid gap-5 md:grid-cols-3">
              {relatedPages.map((page) => (
                <Link key={page.to} to={page.to} className="group rounded-[1.8rem] border border-white/65 bg-white/65 p-6 shadow-card transition-smooth hover:-translate-y-1 hover:bg-white/85">
                  <h3 className="font-display text-3xl leading-none text-foreground group-hover:text-primary">{page.title}</h3>
                  <p className="mt-4 text-sm leading-7 text-foreground/65">{page.helper}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary">Continuer le guide <ArrowRight className="h-4 w-4" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section-shell pb-24 pt-10">
          <div className="container mx-auto px-6 text-center">
            <div className="mx-auto max-w-3xl rounded-[2.2rem] border border-white/65 bg-white/70 p-8 shadow-card backdrop-blur-md md:p-10">
              <Sparkles className="mx-auto mb-5 h-8 w-8 text-primary" />
              <h2 className="font-display text-4xl leading-none text-foreground md:text-5xl">Prêt à créer votre portrait de couple ?</h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-foreground/65">Importez votre photo, choisissez un style artistique et découvrez le rendu avant de sélectionner votre support.</p>
              <Button variant="premium" size="lg" className="mt-7 h-14 rounded-full px-8" asChild>
                <Link to={createPath('final')} onClick={() => trackCoupleCta('final')}>
                  Commencer la création
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  </>
);

export default PortraitCouplePersonnalise;
